import { gsap, ScrollTrigger, prefersReducedMotion } from './motion';

const getViewport = () => document.getElementById('viewport');
const getLoader = () => document.getElementById('loader');
const getHeader = () => document.getElementById('app-header');

const createContext = (done = () => null) => {
    return {
        gsap,
        ScrollTrigger,
        loader: getLoader(),
        viewport: getViewport(),
        header: getHeader(),
        reducedMotion: prefersReducedMotion,
        done
    };
};

const defaultSetup = ({ gsap, loader, viewport }) => {
    if(viewport){
        gsap.set(viewport, {
            autoAlpha: 1,
            pointerEvents: 'initial'
        });
    }

    if(loader){
        gsap.set(loader, {
            autoAlpha: 1
        });
    }
};

const defaultLeaveAnimation = ({ gsap, viewport, header }) => {
    const targets = [viewport, header].filter(Boolean);

    if(!targets.length){
        return null;
    }

    return gsap.to(targets, {
        autoAlpha: 0,
        pointerEvents: 'none',
        duration: 0.2,
        ease: 'power2.out',
        overwrite: 'auto'
    });
};

const defaultLeaveImmediate = ({ gsap, viewport, header }) => {
    const targets = [viewport, header].filter(Boolean);

    if(targets.length){
        gsap.set(targets, {
            autoAlpha: 0,
            pointerEvents: 'none'
        });
    }
};

const defaultEnterAnimation = ({ gsap, viewport, header }) => {
    const targets = [viewport, header].filter(Boolean);

    if(!targets.length){
        return null;
    }

    return gsap.to(targets, {
        autoAlpha: 1,
        pointerEvents: 'initial',
        duration: 0.2,
        ease: 'power2.out',
        overwrite: 'auto'
    });
};

const defaultEnterImmediate = ({ gsap, viewport, header }) => {
    const targets = [viewport, header].filter(Boolean);

    if(targets.length){
        gsap.set(targets, {
            autoAlpha: 1,
            pointerEvents: 'initial'
        });
    }
};

export const PageTransitionAnimation = {
    setupAnimation: defaultSetup,
    leaveAnimation: defaultLeaveAnimation,
    leaveImmediate: defaultLeaveImmediate,
    enterAnimation: defaultEnterAnimation,
    enterImmediate: defaultEnterImmediate,
    configure(){
        return this.reset();
    },
    setSetup(animationFactory){
        this.setupAnimation = typeof animationFactory === 'function'
            ? animationFactory
            : defaultSetup;

        return this;
    },
    setLeave(animationFactory, immediateFactory = null){
        this.leaveAnimation = typeof animationFactory === 'function'
            ? animationFactory
            : defaultLeaveAnimation;

        this.leaveImmediate = typeof immediateFactory === 'function'
            ? immediateFactory
            : animationFactory
                ? null
                : defaultLeaveImmediate;

        return this;
    },
    setEnter(animationFactory, immediateFactory = null){
        this.enterAnimation = typeof animationFactory === 'function'
            ? animationFactory
            : defaultEnterAnimation;

        this.enterImmediate = typeof immediateFactory === 'function'
            ? immediateFactory
            : animationFactory
                ? null
                : defaultEnterImmediate;

        return this;
    },
    reset(){
        this.setupAnimation = defaultSetup;
        this.leaveAnimation = defaultLeaveAnimation;
        this.leaveImmediate = defaultLeaveImmediate;
        this.enterAnimation = defaultEnterAnimation;
        this.enterImmediate = defaultEnterImmediate;
        return this;
    },
    setup(){
        getHeader()?.removeAttribute('inert');
        document.documentElement.classList.remove('is-page-leaving');
        this.setupAnimation?.(createContext());
        return this;
    },
    leave(context = {}){
        getHeader()?.setAttribute('inert', '');
        // Body portals must stay hidden between the outgoing and incoming pages.
        document.documentElement.classList.add('is-page-leaving');
        const animationContext = {
            ...createContext(),
            ...context
        };

        if(prefersReducedMotion && typeof this.leaveImmediate === 'function'){
            this.leaveImmediate(animationContext);
            return null;
        }

        return this.leaveAnimation?.(animationContext) || null;
    },
    enter(context = {}){
        getHeader()?.removeAttribute('inert');
        document.documentElement.classList.remove('is-page-leaving');
        const animationContext = {
            ...createContext(),
            ...context
        };

        if(prefersReducedMotion && typeof this.enterImmediate === 'function'){
            this.enterImmediate(animationContext);
            return null;
        }

        return this.enterAnimation?.(animationContext) || null;
    }
};

export const pageTransition = {
    setup(){
        PageTransitionAnimation.setup();
    },
    leave(){
        return PageTransitionAnimation.leave();
    },
    enter(){
        return PageTransitionAnimation.enter();
    }
};
